
import React, { useState, useRef } from 'react';
import { Button } from './ui/Button';
import { ServiceCategory, PlatformFeeConfig, WorkerProfile } from '../types';
import { Power, IndianRupee, TrendingUp, Shield, Loader2, Check, Clock, AlertTriangle, ShieldCheck } from 'lucide-react';
import { saveWorkerProfile } from '../services/firestoreService';
import { uploadFile } from '../services/apiService';
import { SKILL_TAGS } from '../constants';
import { useTranslation } from '../translations';
import { WorkerPortfolio } from './WorkerPortfolio';
import { WorkerWallet } from './WorkerWallet';
import { NotificationBell } from './NotificationBell';
import { UserRole } from '../types';

interface WorkerDashboardProps {
    categories: ServiceCategory[];
    feeConfig: PlatformFeeConfig;
    currentWorker: WorkerProfile;
    onUpdateWorker: (worker: WorkerProfile) => void;
    defaultTab?: 'stats' | 'profile' | 'verification';
}

export const WorkerDashboard: React.FC<WorkerDashboardProps> = ({ categories, feeConfig, currentWorker, onUpdateWorker, defaultTab = 'stats' }) => {
    const [activeTab, setActiveTab] = useState<'stats' | 'profile' | 'verification'>(defaultTab);

    React.useEffect(() => {
        if (defaultTab) setActiveTab(defaultTab);
    }, [defaultTab]);

    const [isSaving, setIsSaving] = useState(false);
    const [isUploadingID, setIsUploadingID] = useState(false);
    const [verificationError, setVerificationError] = useState<string | null>(null);

    const [tempBio, setTempBio] = useState(currentWorker?.bio || '');
    const [tempSkill, setTempSkill] = useState(currentWorker?.skill || '');
    const [tempTags, setTempTags] = useState<string[]>(currentWorker?.tags || []);
    const [showWallet, setShowWallet] = useState(false);

    const lang = (localStorage.getItem('sl_lang') || 'en') as any;
    const t = useTranslation(lang);

    const idProofRef = useRef<HTMLInputElement>(null);

    // Guard Clause for missing data during logout
    if (!currentWorker) return (
        <div className="h-full bg-slate-50 flex items-center justify-center">
            <div className="text-center animate-pulse">
                <div className="w-16 h-16 bg-slate-200 rounded-2xl mx-auto mb-4" />
                <div className="h-4 w-32 bg-slate-200 rounded mx-auto mb-2" />
                <div className="h-3 w-48 bg-slate-200 rounded mx-auto" />
                <p className="mt-8 text-xs font-black text-slate-400 uppercase tracking-widest">Initializing Expert Identity...</p>
            </div>
        </div>
    );

    // BLOCKING UI REMOVED: Allowing full access so verified status can be handled via banners/disabled actions
    // if (currentWorker.verificationStatus === 'pending') { ... }

    const handleSaveProfile = async () => {
        setIsSaving(true);
        try {
            console.log("Attempting to save profile...");
            const updated = { ...currentWorker, bio: tempBio, tags: tempTags, skill: tempSkill };
            await saveWorkerProfile(updated);
            onUpdateWorker(updated);
            alert("Profile saved successfully!");
        } catch (err: any) {
            console.error("Save failed:", err);
            alert(`Failed to save profile: ${err.message || 'Unknown error'}`);
        } finally {
            setIsSaving(false);
        }
    };

    const handleIdUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsUploadingID(true);
        setVerificationError(null);

        try {
            // 1. Upload & Compress
            const base64Image = await uploadFile(file);

            // 2. Save and mark as pending for admin verification
            const updated = {
                ...currentWorker,
                idProofUrl: base64Image,
                verificationStatus: 'pending' as const
            };
            await saveWorkerProfile(updated);
            onUpdateWorker(updated);
            alert("ID Proof uploaded successfully! Waiting for admin verification.");
        } catch (error: any) {
            console.error(error);
            setVerificationError("Upload Failed. Please try again with a clear image.");
        } finally {
            setIsUploadingID(false);
        }
    };



    const toggleTag = (tag: string) => {
        setTempTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
    };

    const toggleOnline = async () => {
        // Subscription Gating (New Model)
        const subEnd = currentWorker.subscriptionEnd ? new Date(currentWorker.subscriptionEnd) : new Date(0);
        if (subEnd < new Date()) {
            alert("Subscription Expired! Please contact Admin/Support to renew your plan.");
            return;
        }

        if (currentWorker.verificationStatus !== 'verified') {
            alert("You must be verified by admin to go online.");
            return;
        }


        try {
            const updated = { ...currentWorker, isOnline: !currentWorker.isOnline };
            // Optimistic update
            onUpdateWorker(updated);
            await saveWorkerProfile(updated);
        } catch (err: any) {
            console.error("Toggle online failed:", err);
            alert("Failed to update status. Check connection.");
            // Revert optimistic update
            onUpdateWorker({ ...currentWorker, isOnline: !currentWorker.isOnline });
        }
    };

    const availableSkillTags = tempSkill ? SKILL_TAGS[tempSkill] || [] : [];


    // Cover Image Logic
    const [isUploadingCover, setIsUploadingCover] = useState(false);
    const coverInputRef = useRef<HTMLInputElement>(null);

    const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsUploadingCover(true);
        try {
            const coverUrl = await uploadFile(file);
            const updated = { ...currentWorker, coverImageUrl: coverUrl };
            await saveWorkerProfile(updated);
            onUpdateWorker(updated);
            alert("Cover image updated successfully!");
        } catch (error: any) {
            console.error("Cover upload failed:", error);
            alert("Failed to upload cover image.");
        } finally {
            setIsUploadingCover(false);
        }
    };

    // Background Location Tracking
    React.useEffect(() => {
        if (currentWorker.isOnline && currentWorker.verificationStatus === 'verified') {
            const watchId = navigator.geolocation.watchPosition(
                (pos) => {
                    import('../services/firestoreService').then(({ updateWorkerLocation }) => {
                        updateWorkerLocation(currentWorker.id, pos.coords.latitude, pos.coords.longitude);
                    });
                },
                (err) => console.error("Location Denied", err),
                { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 }
            );
            return () => navigator.geolocation.clearWatch(watchId);
        }
    }, [currentWorker.isOnline, currentWorker.verificationStatus, currentWorker.id]);

    return (
        <div className="h-full bg-slate-50 dark:bg-slate-950 flex flex-col overflow-hidden relative">
            <div className="bg-white dark:bg-slate-900 px-6 py-5 border-b border-slate-100 dark:border-slate-800 shrink-0">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="relative">
                            <img
                                src={currentWorker.avatarUrl || 'https://cdn-icons-png.flaticon.com/512/149/149071.png'}
                                className="w-12 h-12 rounded-xl object-cover shadow-sm bg-slate-50 dark:bg-slate-800"
                                alt="avatar"
                            />
                            <div className={`absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full border-2 border-white dark:border-slate-900 ${currentWorker.isOnline ? 'bg-green-500' : 'bg-slate-300 dark:bg-slate-600'}`}></div>
                        </div>
                        <div>
                            <h1 className="text-lg font-black text-slate-900 dark:text-white tracking-tight leading-none">{currentWorker.name}</h1>
                            <div className="flex items-center gap-2 mt-1.5">
                                <span className="text-[10px] font-black text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded-md uppercase">{currentWorker.skill}</span>
                                {currentWorker && (currentWorker as any).verificationStatus === 'pending' && <span className="text-[8px] font-bold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 rounded-md uppercase">Pending Verification</span>}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <NotificationBell userRole={UserRole.WORKER} />
                        <button onClick={toggleOnline} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all font-black text-[10px] uppercase tracking-widest ${currentWorker.isOnline ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 shadow-lg' : 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500'}`}>
                            <Power className="w-4 h-4" /> {currentWorker.isOnline ? 'Online' : 'Offline'}
                        </button>
                    </div>
                </div>

                <div className="flex gap-2 bg-slate-100 dark:bg-slate-800 p-1 rounded-xl overflow-x-auto scrollbar-hide">
                    <button onClick={() => setActiveTab('stats')} className={`flex-1 min-w-[70px] py-2 text-[10px] font-black uppercase rounded-lg transition-all ${activeTab === 'stats' ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm' : 'text-slate-400 dark:text-slate-500'}`}>Stats</button>
                    <button onClick={() => setActiveTab('profile')} className={`flex-1 min-w-[70px] py-2 text-[10px] font-black uppercase rounded-lg transition-all ${activeTab === 'profile' ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm' : 'text-slate-400 dark:text-slate-500'}`}>Profile</button>
                    <button onClick={() => setActiveTab('verification')} className={`flex-1 min-w-[70px] py-2 text-[10px] font-black uppercase rounded-lg transition-all ${activeTab === 'verification' ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm' : 'text-slate-400 dark:text-slate-500'}`}>Auth</button>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto scrollbar-hide">
                <div className="p-6 max-w-4xl mx-auto space-y-6">
                    {activeTab === 'stats' && (
                        <div className="space-y-4">
                            {/* Subscription Status Card */}
                            <div className="bg-indigo-900 dark:bg-indigo-950 text-white p-6 rounded-[2rem] shadow-xl relative overflow-hidden ring-1 ring-white/10">
                                <div className="absolute top-0 right-0 p-6 opacity-10">
                                    <Clock className="w-24 h-24" />
                                </div>
                                <div className="relative z-10">
                                    <p className="text-[10px] font-black uppercase tracking-widest text-indigo-300 mb-1">My Subscription</p>
                                    <h2 className="text-2xl font-black capitalize mb-2">{currentWorker.subscriptionPlan || 'Trial'} Plan</h2>
                                    <p className="text-xs font-bold opacity-80 flex items-center gap-2">
                                        <span className={`w-2 h-2 rounded-full ${new Date(currentWorker.subscriptionEnd || 0) > new Date() ? 'bg-green-400' : 'bg-red-400'}`}></span>
                                        Valid until: {currentWorker.subscriptionEnd ? new Date(currentWorker.subscriptionEnd).toLocaleDateString() : 'N/A'}
                                    </p>
                                </div>
                            </div>
                            {(currentWorker.verificationStatus === 'pending' || currentWorker.verificationStatus === 'unverified') && (
                                <div className={`p-4 rounded-2xl flex items-center gap-4 border ${currentWorker.verificationStatus === 'pending' ? 'bg-amber-50 dark:bg-amber-900/10 border-amber-100 dark:border-amber-900/30 text-amber-800 dark:text-amber-400' : 'bg-red-50 dark:bg-red-900/10 border-red-100 dark:border-red-900/30 text-red-800 dark:text-red-400'}`}>
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${currentWorker.verificationStatus === 'pending' ? 'bg-amber-100 dark:bg-amber-900/40' : 'bg-red-100 dark:bg-red-900/40'}`}>
                                        <ShieldCheck className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-xs font-black uppercase tracking-wider">{currentWorker.verificationStatus === 'pending' ? 'Verification Pending' : 'Action Required'}</h3>
                                        <p className="text-[10px] font-bold opacity-70 mt-1">
                                            {currentWorker.verificationStatus === 'pending'
                                                ? 'You can edit your profile while waiting for approval.'
                                                : 'Please upload ID proof in the Auth tab to start earning.'}
                                        </p>
                                    </div>
                                    <button onClick={() => setActiveTab('verification')} className="bg-white dark:bg-slate-800 px-3 py-2 rounded-lg text-[9px] font-black uppercase shadow-sm">View</button>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div
                                    onClick={() => setShowWallet(true)}
                                    className="bg-slate-900 dark:bg-slate-800 p-8 rounded-[2rem] text-white shadow-xl cursor-pointer hover:scale-[1.02] transition-transform active:scale-95 border border-slate-800 dark:border-slate-700"
                                >
                                    <div className="flex justify-between items-start mb-4">
                                        <p className="text-[9px] font-black text-blue-400 uppercase tracking-widest">Net Earnings</p>
                                        <IndianRupee className="w-5 h-5 text-white opacity-20" />
                                    </div>
                                    <h2 className="text-4xl font-black">₹{currentWorker.totalEarned?.toLocaleString()}</h2>
                                    <Button
                                        size="sm"
                                        onClick={(e) => { e.stopPropagation(); setShowWallet(true); }}
                                        className="bg-white !text-slate-900 mt-6 rounded-lg w-full hover:bg-slate-100 border-none font-black"
                                        style={{ color: '#0f172a' }}
                                    >
                                        Earnings Log
                                    </Button>
                                </div>
                                <div className="bg-white dark:bg-slate-900 p-8 rounded-[2rem] border border-slate-100 dark:border-slate-800 shadow-sm">
                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Jobs Done</p>
                                    <h2 className="text-4xl font-black text-slate-900 dark:text-white">{currentWorker.lifetimeJobsCompleted}</h2>
                                    <div className="flex items-center gap-1.5 text-green-600 dark:text-green-400 font-black text-[10px] mt-4 uppercase">
                                        <TrendingUp className="w-4 h-4" /> 100% Reliable
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'profile' && (
                        <div className="space-y-6">


                            <div className="bg-white dark:bg-slate-900 p-6 rounded-[2rem] border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="font-black text-slate-900 dark:text-white uppercase text-[10px]">Expert Bio</h3>
                                    <button onClick={handleSaveProfile} className="bg-blue-600 text-white px-4 py-1.5 rounded-lg text-[10px] font-black uppercase shadow-lg shadow-blue-600/20 active:scale-95 transition-all">
                                        {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save Changes'}
                                    </button>
                                </div>
                                <textarea value={tempBio} onChange={(e) => setTempBio(e.target.value)} className="w-full p-4 bg-slate-50 dark:bg-slate-800 rounded-xl text-sm font-medium h-32 outline-none focus:bg-white dark:focus:bg-slate-700 border-2 border-transparent focus:border-blue-100 dark:focus:border-blue-900/30 transition-all mb-6 dark:text-white" />

                                <div className="mb-6">
                                    <h3 className="font-black text-slate-900 dark:text-white uppercase text-[10px] mb-3">Primary Service</h3>
                                    <select
                                        value={tempSkill}
                                        onChange={e => {
                                            setTempSkill(e.target.value);
                                            setTempTags([]); // Clear tags as tags are skill-specific
                                        }}
                                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl px-5 py-4 font-black text-sm text-slate-900 dark:text-white focus:bg-white dark:focus:bg-slate-700 focus:border-blue-600 outline-none transition-all shadow-sm appearance-none"
                                    >
                                        <option value="">Select Primary Service</option>
                                        {categories.filter(c => c.isActive && !c.parentId).map(parent => (
                                            <optgroup key={parent.id} label={parent.name.toUpperCase()}>
                                                <option value={parent.name}>{parent.name} (General)</option>
                                                {categories.filter(sub => sub.parentId === parent.id && sub.isActive).map(sub => (
                                                    <option key={sub.id} value={sub.name}>-- {sub.name}</option>
                                                ))}
                                            </optgroup>
                                        ))}
                                    </select>
                                </div>

                                {availableSkillTags.length > 0 && (
                                    <div>
                                        <h3 className="font-black text-slate-900 dark:text-white uppercase text-[10px] mb-3">Skill Tags</h3>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                            {availableSkillTags.map(tag => {
                                                const isSelected = tempTags.includes(tag);
                                                return (
                                                    <button
                                                        key={tag}
                                                        onClick={() => toggleTag(tag)}
                                                        className={`flex items-center justify-between px-4 py-3 rounded-xl text-[10px] font-black uppercase border transition-all ${isSelected
                                                            ? 'bg-blue-600 border-blue-600 text-white shadow-md'
                                                            : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600'
                                                            }`}
                                                    >
                                                        <span>{tag}</span>
                                                        {isSelected && <Check className="w-3 h-3" />}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden">
                                <WorkerPortfolio worker={currentWorker} onUpdate={onUpdateWorker} />
                            </div>
                        </div>
                    )}

                    {activeTab === 'verification' && (
                        <div className="bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] border border-slate-100 dark:border-slate-800 text-center shadow-sm max-w-md mx-auto">
                            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <Shield className="w-8 h-8" />
                            </div>
                            <h3 className="text-xl font-black text-slate-900 dark:text-white">Expert Auth</h3>
                            <p className="text-sm text-slate-400 dark:text-slate-500 mt-2 mb-6 font-medium">
                                Status: <span className={`uppercase font-black ${currentWorker.verificationStatus === 'verified' ? 'text-green-600 dark:text-green-400' : 'text-amber-500'}`}>{currentWorker.verificationStatus}</span>
                            </p>

                            {verificationError && (
                                <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-xl text-xs font-bold border border-red-100 dark:border-red-900/30 flex items-start gap-2 text-left">
                                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                                    {verificationError}
                                </div>
                            )}

                            {currentWorker.idProofUrl ? (
                                <div className="space-y-4">
                                    <div className="aspect-video bg-slate-50 dark:bg-slate-800 rounded-2xl overflow-hidden border dark:border-slate-700">
                                        <img
                                            src={currentWorker.idProofUrl || 'https://via.placeholder.com/400x300?text=ID+Proof'}
                                            className="w-full h-full object-cover"
                                            alt="ID Proof"
                                        />
                                    </div>
                                    {currentWorker.verificationStatus !== 'verified' && (
                                        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 text-xs font-bold rounded-xl flex items-center gap-2">
                                            <Clock className="w-4 h-4" /> Admin Review Pending
                                        </div>
                                    )}
                                    {/* Allow re-upload even if pending, in case they uploaded wrong doc */}
                                    <Button variant="outline" fullWidth onClick={() => idProofRef.current?.click()} disabled={isUploadingID} className="dark:bg-slate-800 dark:text-white dark:border-slate-700">
                                        {isUploadingID ? <Loader2 className="animate-spin" /> : 'Re-Upload ID'}
                                    </Button>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="text-xs text-slate-500 dark:text-slate-400 font-bold bg-slate-50 dark:bg-slate-800 p-4 rounded-xl">
                                        Accepted Documents:
                                        <ul className="list-disc list-inside mt-2 text-slate-400 dark:text-slate-500 text-[10px] uppercase tracking-wider">
                                            <li>Aadhaar Card (Original)</li>
                                            <li>Voter ID Card</li>
                                        </ul>
                                    </div>
                                    <Button fullWidth onClick={() => idProofRef.current?.click()} disabled={isUploadingID}>
                                        {isUploadingID ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Verifying...</> : <><ShieldCheck className="w-4 h-4 mr-2" /> Upload ID Signal</>}
                                    </Button>
                                </div>
                            )}
                            <input type="file" ref={idProofRef} className="hidden" onChange={handleIdUpload} accept="image/*" />

                        </div>
                    )}
                </div>
            </div>

            {showWallet && (
                <WorkerWallet worker={currentWorker} onClose={() => setShowWallet(false)} />
            )}
        </div>
    );
};
